import { jsPDF } from 'jspdf';
import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export const PDF_STORAGE_MAX_BYTES = 2 * 1024 * 1024;
export const PDF_COMPRESSION_INPUT_MAX_BYTES = 20 * 1024 * 1024;
export const PDF_COMPRESSION_MAX_PAGES = 100;

const compressionProfiles = [
  { dpi: 144, jpegQuality: 0.82 },
  { dpi: 120, jpegQuality: 0.72 },
  { dpi: 96, jpegQuality: 0.62 },
];

export class PdfCompressionError extends Error {
  constructor(code, message = code) {
    super(message);
    this.name = 'PdfCompressionError';
    this.code = code;
  }
}

function throwIfCancelled(isCancelled) {
  if (isCancelled && isCancelled()) {
    throw new PdfCompressionError('cancelled');
  }
}

function isTypedCompressionError(error) {
  return error instanceof PdfCompressionError;
}

function asRenderError(error) {
  if (isTypedCompressionError(error)) {
    return error;
  }
  if (error && error.name === 'PasswordException') {
    return new PdfCompressionError('password-protected');
  }
  return new PdfCompressionError('render-failed');
}

function canvasToJpegBytes(canvas, quality, isCancelled) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        try {
          throwIfCancelled(isCancelled);
          if (!blob) {
            reject(new PdfCompressionError('render-failed'));
            return;
          }
          blob.arrayBuffer()
            .then((arrayBuffer) => {
              try {
                throwIfCancelled(isCancelled);
                resolve(new Uint8Array(arrayBuffer));
              } catch (error) {
                reject(error);
              }
            })
            .catch((error) => reject(error));
        } catch (error) {
          reject(error);
        }
      }, 'image/jpeg', quality);
    } catch (error) {
      reject(error);
    }
  });
}

function waitForRenderTask(renderTask, isCancelled) {
  if (!isCancelled) {
    return renderTask.promise;
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let cancellationInterval;
    const finish = (callback, value) => {
      if (!settled) {
        settled = true;
        clearInterval(cancellationInterval);
        callback(value);
      }
    };
    const cancelIfRequested = () => {
      if (isCancelled()) {
        if (typeof renderTask.cancel === 'function') {
          try {
            renderTask.cancel();
          } catch (error) {
            // PDF.js can throw when a render task has already settled.
          }
        }
        finish(reject, new PdfCompressionError('cancelled'));
      }
    };

    cancellationInterval = setInterval(cancelIfRequested, 50);
    renderTask.promise.then(
      () => finish(resolve),
      (error) => finish(reject, error),
    );
    cancelIfRequested();
  });
}

function createPdfDocument(width, height) {
  // eslint-disable-next-line new-cap
  return new jsPDF({
    compress: true,
    format: [width, height],
    orientation: width > height ? 'landscape' : 'portrait',
    unit: 'pt',
  });
}

async function renderProfile(pdf, profile, profileIndex, onProgress, isCancelled) {
  let output;
  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      throwIfCancelled(isCancelled);
      let page;
      let canvas;
      let cleanupError;
      try {
        // The pages must be rendered one at a time to keep browser memory bounded.
        // eslint-disable-next-line no-await-in-loop
        page = await pdf.getPage(pageNumber);
        const originalViewport = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: profile.dpi / 72 });
        canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const context = canvas.getContext('2d', { alpha: false });

        if (!context) {
          throw new PdfCompressionError('render-failed');
        }

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        const renderTask = page.render({ canvasContext: context, viewport });
        // eslint-disable-next-line no-await-in-loop
        await waitForRenderTask(renderTask, isCancelled);
        throwIfCancelled(isCancelled);

        // eslint-disable-next-line no-await-in-loop
        const imageBytes = await canvasToJpegBytes(canvas, profile.jpegQuality, isCancelled);
        throwIfCancelled(isCancelled);
        if (!output) {
          output = createPdfDocument(originalViewport.width, originalViewport.height);
        } else {
          output.addPage(
            [originalViewport.width, originalViewport.height],
            originalViewport.width > originalViewport.height ? 'landscape' : 'portrait',
          );
        }
        throwIfCancelled(isCancelled);
        output.addImage(
          imageBytes,
          'JPEG',
          0,
          0,
          originalViewport.width,
          originalViewport.height,
          undefined,
          'FAST',
        );
        if (onProgress) {
          onProgress({
            pageNumber,
            pageCount: pdf.numPages,
            profileIndex,
            profileCount: compressionProfiles.length,
          });
        }
      } finally {
        try {
          if (canvas) {
            canvas.width = 0;
            canvas.height = 0;
          }
          if (page) {
            page.cleanup();
          }
        } catch (error) {
          cleanupError = error;
        }
      }
      if (cleanupError) {
        throw cleanupError;
      }
    }

    throwIfCancelled(isCancelled);
    return output.output('blob');
  } catch (error) {
    throw asRenderError(error);
  }
}

export function formatFileSize(size) {
  return `${(size / (1024 * 1024)).toFixed(2)} MiB`;
}

export function compressedFileName(name) {
  const baseName = name.replace(/\.pdf$/i, '') || 'certificat';
  return `${baseName}-optimise.pdf`;
}

export function getPdfCompressionErrorCode(error) {
  if (error instanceof PdfCompressionError) {
    return error.code;
  }
  if (error && error.name === 'PasswordException') {
    return 'password-protected';
  }
  return 'invalid-pdf';
}

export async function compressPdf(file, { onProgress, isCancelled } = {}) {
  if (file.size > PDF_COMPRESSION_INPUT_MAX_BYTES) {
    throw new PdfCompressionError('source-too-large');
  }

  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  let pdf;
  try {
    pdf = await loadingTask.promise;
    if (pdf.numPages > PDF_COMPRESSION_MAX_PAGES) {
      throw new PdfCompressionError('too-many-pages');
    }

    for (let profileIndex = 0; profileIndex < compressionProfiles.length; profileIndex += 1) {
      throwIfCancelled(isCancelled);
      // Profiles are intentionally attempted in order, from most to least detailed.
      // eslint-disable-next-line no-await-in-loop
      const blob = await renderProfile(pdf, compressionProfiles[profileIndex], profileIndex, onProgress, isCancelled);
      throwIfCancelled(isCancelled);
      if (blob.size <= PDF_STORAGE_MAX_BYTES) {
        throwIfCancelled(isCancelled);
        return {
          blob,
          pageCount: pdf.numPages,
          profile: compressionProfiles[profileIndex],
        };
      }
    }
    throw new PdfCompressionError('could-not-compress');
  } catch (error) {
    if (error && error.name === 'PasswordException') {
      throw new PdfCompressionError('password-protected');
    }
    throw error;
  } finally {
    try {
      if (pdf) {
        await pdf.destroy();
      } else {
        await loadingTask.destroy();
      }
    } catch {
      // Never let best-effort resource disposal hide the compression result or error.
    }
  }
}

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = () => reject(new PdfCompressionError('read-failed'));
    reader.readAsDataURL(file);
  });
}
